import { useEffect, useState } from 'react'

import type { CatalogImportRowFieldOverride, ImportPreviewRowDto } from '../api/catalogImportTypes'
import type { ProductTypeDto } from '../api/productTypes'
import { ProductTypeCascadePicker } from './ProductTypeCascadePicker'
import { SuggestTextField } from './SuggestTextField'

type ImportRowEditPanelProps = {
  row: ImportPreviewRowDto
  selectedProductTypeId: number | null
  fieldOverride: CatalogImportRowFieldOverride | null
  productTypes: ProductTypeDto[]
  brandOptions?: string[]
  countryOptions?: string[]
  onTypeChange: (productTypeId: number | null) => void
  onFieldOverrideChange: (override: CatalogImportRowFieldOverride | null) => void
}

const buildFieldOverride = (
  row: ImportPreviewRowDto,
  name: string,
  brand: string,
  country: string,
  price: string,
  unit: string,
  minShipmentQty: string,
  packageSize: string,
  comment: string,
): CatalogImportRowFieldOverride | null => {
  const originalName = row.name?.trim() ?? ''
  const originalBrand = row.brand?.trim() ?? ''
  const originalCountry = row.country?.trim() ?? ''
  const originalPrice = row.price?.trim() ?? ''
  const originalUnit = row.unit?.trim() ?? ''
  const originalMinShipment = row.minShipmentQty?.trim() ?? ''
  const originalPackageSize = row.packageSize?.trim() ?? ''
  const nameChanged = name.trim() !== originalName
  const brandChanged = brand.trim() !== originalBrand
  const countryChanged = country.trim() !== originalCountry
  const priceChanged = price.trim() !== originalPrice
  const unitChanged = unit.trim() !== originalUnit
  const minShipmentChanged = minShipmentQty.trim() !== originalMinShipment
  const packageSizeChanged = packageSize.trim() !== originalPackageSize
  const commentChanged = comment.trim().length > 0

  if (
    !nameChanged &&
    !brandChanged &&
    !countryChanged &&
    !priceChanged &&
    !unitChanged &&
    !minShipmentChanged &&
    !packageSizeChanged &&
    !commentChanged
  ) {
    return null
  }

  return {
    name: nameChanged ? name.trim() : undefined,
    brand: brandChanged ? brand.trim() : undefined,
    country: countryChanged ? country.trim() : undefined,
    price: priceChanged ? price.trim() : undefined,
    unit: unitChanged ? unit.trim() : undefined,
    minShipmentQty: minShipmentChanged ? minShipmentQty.trim() : undefined,
    packageSize: packageSizeChanged ? packageSize.trim() : undefined,
    comment: commentChanged ? comment.trim() : undefined,
  }
}

type CorrectionReasonHint = {
  value: 'wrong_brand' | 'wrong_species_type' | 'wrong_packaging' | 'wrong_price' | 'not_in_dictionary' | 'other'
  label: string
}

const REASON_LABELS: Record<CorrectionReasonHint['value'], string> = {
  wrong_brand: 'Бренд',
  wrong_species_type: 'Вид / сорт',
  wrong_packaging: 'Фасовка',
  wrong_price: 'Цена',
  not_in_dictionary: 'Нет в справочнике',
  other: 'Другое',
}

const normalizeForDiff = (value: string | null | undefined): string =>
  (value ?? '').trim().toLocaleLowerCase('ru-RU')

/**
 * Client-side mirror of the backend's CatalogMatchCorrectionReasonResolver — an
 * explanatory hint shown while editing only. The backend independently re-derives the
 * same reason from its own diff on confirm; nothing here is sent to the server.
 */
const inferCorrectionReason = (
  row: ImportPreviewRowDto,
  selectedProductTypeId: number | null,
  brand: string,
  packageSize: string,
  price: string,
): CorrectionReasonHint | null => {
  if (row.matchPreview.candidates.length === 0) {
    return { value: 'not_in_dictionary', label: REASON_LABELS.not_in_dictionary }
  }

  const defaultTypeId = row.matchPreview.candidates[0]?.productTypeId ?? null
  const typeChanged = selectedProductTypeId !== defaultTypeId
  const brandChanged = normalizeForDiff(brand) !== normalizeForDiff(row.brand)
  const packageChanged = normalizeForDiff(packageSize) !== normalizeForDiff(row.packageSize)
  const priceChanged = normalizeForDiff(price) !== normalizeForDiff(row.price)
  const changedCount = [typeChanged, brandChanged, packageChanged, priceChanged].filter(Boolean).length

  if (changedCount === 0) {
    return null
  }
  if (changedCount > 1) {
    return { value: 'other', label: REASON_LABELS.other }
  }
  if (typeChanged) {
    return { value: 'wrong_species_type', label: REASON_LABELS.wrong_species_type }
  }
  if (brandChanged) {
    return { value: 'wrong_brand', label: REASON_LABELS.wrong_brand }
  }
  return packageChanged
    ? { value: 'wrong_packaging', label: REASON_LABELS.wrong_packaging }
    : { value: 'wrong_price', label: REASON_LABELS.wrong_price }
}

export const ImportRowEditPanel = ({
  row,
  selectedProductTypeId,
  fieldOverride,
  productTypes,
  brandOptions = [],
  countryOptions = [],
  onTypeChange,
  onFieldOverrideChange,
}: ImportRowEditPanelProps) => {
  const [name, setName] = useState(fieldOverride?.name ?? row.name ?? '')
  const [brand, setBrand] = useState(fieldOverride?.brand ?? row.brand ?? '')
  const [country, setCountry] = useState(fieldOverride?.country ?? row.country ?? '')
  const [price, setPrice] = useState(fieldOverride?.price ?? row.price ?? '')
  const [unit, setUnit] = useState(fieldOverride?.unit ?? row.unit ?? '')
  const [minShipmentQty, setMinShipmentQty] = useState(
    fieldOverride?.minShipmentQty ?? row.minShipmentQty ?? '',
  )
  const [packageSize, setPackageSize] = useState(fieldOverride?.packageSize ?? row.packageSize ?? '')
  const [comment, setComment] = useState(fieldOverride?.comment ?? '')

  const correctionReason = inferCorrectionReason(row, selectedProductTypeId, brand, packageSize, price)

  useEffect(() => {
    setName(fieldOverride?.name ?? row.name ?? '')
    setBrand(fieldOverride?.brand ?? row.brand ?? '')
    setCountry(fieldOverride?.country ?? row.country ?? '')
    setPrice(fieldOverride?.price ?? row.price ?? '')
    setUnit(fieldOverride?.unit ?? row.unit ?? '')
    setMinShipmentQty(fieldOverride?.minShipmentQty ?? row.minShipmentQty ?? '')
    setPackageSize(fieldOverride?.packageSize ?? row.packageSize ?? '')
    setComment(fieldOverride?.comment ?? '')
  }, [
    row.rowNumber,
    fieldOverride,
    row.name,
    row.brand,
    row.country,
    row.price,
    row.unit,
    row.minShipmentQty,
    row.packageSize,
  ])

  const notifyFieldOverride = (
    nextName: string,
    nextBrand: string,
    nextCountry: string,
    nextPrice: string,
    nextUnit: string,
    nextMinShipmentQty: string,
    nextPackageSize: string,
    nextComment: string,
  ) => {
    onFieldOverrideChange(
      buildFieldOverride(
        row,
        nextName,
        nextBrand,
        nextCountry,
        nextPrice,
        nextUnit,
        nextMinShipmentQty,
        nextPackageSize,
        nextComment,
      ),
    )
  }

  return (
    <div className="market-catalog-edit">
      <div className="market-catalog-edit__meta">
        {row.externalSku ? <span>Артикул: {row.externalSku}</span> : null}
        {row.unit ? <span>Ед.: {row.unit}</span> : null}
        {row.minShipmentQty ? <span>Мин. отгрузка: {row.minShipmentQty}</span> : null}
        {row.packageSize ? <span>Упаковка: {row.packageSize}</span> : null}
        {row.category ? <span>Категория из файла: {row.category}</span> : null}
      </div>

      <div className="market-catalog-edit__grid">
        <label className="echat-field market-catalog-edit__field--wide">
          <span className="echat-field__label">Наименование</span>
          <input
            className="echat-field__input"
            value={name}
            required
            onChange={(event) => {
              const value = event.target.value
              setName(value)
              notifyFieldOverride(value, brand, country, price, unit, minShipmentQty, packageSize, comment)
            }}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Цена</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={price}
            onChange={(event) => {
              const value = event.target.value
              setPrice(value)
              notifyFieldOverride(name, brand, country, value, unit, minShipmentQty, packageSize, comment)
            }}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Ед. изм.</span>
          <input
            className="echat-field__input"
            value={unit}
            onChange={(event) => {
              const value = event.target.value
              setUnit(value)
              notifyFieldOverride(name, brand, country, price, value, minShipmentQty, packageSize, comment)
            }}
          />
        </label>
        <SuggestTextField
          label="Бренд"
          value={brand}
          suggestions={brandOptions}
          listId={`import-row-${row.rowNumber}-brand`}
          onChange={(value) => {
            setBrand(value)
            notifyFieldOverride(name, value, country, price, unit, minShipmentQty, packageSize, comment)
          }}
        />
        <SuggestTextField
          label="Страна"
          value={country}
          suggestions={countryOptions}
          listId={`import-row-${row.rowNumber}-country`}
          onChange={(value) => {
            setCountry(value)
            notifyFieldOverride(name, brand, value, price, unit, minShipmentQty, packageSize, comment)
          }}
        />
        <label className="echat-field">
          <span className="echat-field__label">Мин. отгрузка</span>
          <input
            className="echat-field__input"
            inputMode="decimal"
            value={minShipmentQty}
            onChange={(event) => {
              const value = event.target.value
              setMinShipmentQty(value)
              notifyFieldOverride(name, brand, country, price, unit, value, packageSize, comment)
            }}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Упаковка</span>
          <input
            className="echat-field__input"
            value={packageSize}
            onChange={(event) => {
              const value = event.target.value
              setPackageSize(value)
              notifyFieldOverride(name, brand, country, price, unit, minShipmentQty, value, comment)
            }}
          />
        </label>
        <ProductTypeCascadePicker
          productTypes={productTypes}
          value={selectedProductTypeId}
          onChange={onTypeChange}
          className="market-product-type-cascade market-product-type-cascade--form"
          emptyLabel="— из матчинга —"
          idPrefix={`import-row-${row.rowNumber}`}
        />
      </div>

      {row.matchPreview.candidates.length > 0 ? (
        <div className="market-catalog-edit__suggestions">
          <p className="echat-field__label">Подсказки матчера</p>
          <div className="market-inline-actions">
            {row.matchPreview.candidates.slice(0, 5).map((candidate) => (
              <button
                key={candidate.productTypeId}
                type="button"
                className="echat-btn echat-btn--ghost echat-btn--sm"
                onClick={() => onTypeChange(candidate.productTypeId)}
              >
                {candidate.name} ({Math.round(candidate.score * 100)}%)
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {correctionReason ? (
        <div className="market-catalog-edit__suggestions">
          <p className="echat-field__label">Причина правки</p>
          <span className="badge">{correctionReason.label}</span>
          {correctionReason.value === 'other' ? (
            <p className="echat-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 4 }}>
              Изменилось несколько полей сразу — уточните, что не так, в комментарии ниже.
            </p>
          ) : null}
        </div>
      ) : null}

      <label className="echat-field">
        <span className="echat-field__label">Комментарий к строке</span>
        <textarea
          className="echat-field__input"
          rows={2}
          value={comment}
          onChange={(event) => {
            const value = event.target.value
            setComment(value)
            notifyFieldOverride(name, brand, country, price, unit, minShipmentQty, packageSize, value)
          }}
        />
      </label>
    </div>
  )
}

export const effectiveImportRowTypeId = (
  row: ImportPreviewRowDto,
  rowTypeOverrides: Record<number, number>,
): number | null => {
  const override = rowTypeOverrides[row.rowNumber]
  if (override != null) {
    return override
  }
  return row.matchPreview.candidates[0]?.productTypeId ?? null
}

/**
 * Types to send on confirm: only explicit user edits.
 * Do not pin preview rematch results — on price-list reimport that would overwrite
 * existing categories (manual/auto/suggested) via backend override → manual path.
 * Unedited rows rematch or preserve on the server (CatalogMatchStatusPolicy).
 */
export const buildConfirmProductTypeOverrides = (
  _rows: ImportPreviewRowDto[],
  rowTypeOverrides: Record<number, number>,
  excludedRowNumbers: ReadonlySet<number> = new Set(),
): Record<number, number> => {
  const overrides: Record<number, number> = {}
  for (const [rowKey, typeId] of Object.entries(rowTypeOverrides)) {
    const rowNumber = Number(rowKey)
    if (!Number.isFinite(rowNumber) || excludedRowNumbers.has(rowNumber)) {
      continue
    }
    overrides[rowNumber] = typeId
  }
  return overrides
}

export const importRowTypeLabel = (
  row: ImportPreviewRowDto,
  rowTypeOverrides: Record<number, number>,
  typeNames: Map<number, string>,
): string => {
  const typeId = effectiveImportRowTypeId(row, rowTypeOverrides)
  if (typeId == null) {
    return '—'
  }
  const best = row.matchPreview.candidates.find((candidate) => candidate.productTypeId === typeId)
  const name = typeNames.get(typeId) ?? best?.name
  if (name && best) {
    return `${name} (${Math.round(best.score * 100)}%)`
  }
  return name ?? `Тип #${typeId}`
}
